import type {
  ScrapeResult,
  DetectionResult,
  PageFacts,
  GenerationResult,
  RateLimitInfo,
  SchemaScore,
} from '../types';

export class RateLimitError extends Error {
  info: RateLimitInfo;
  constructor(info: RateLimitInfo) {
    super(info.message);
    this.name = 'RateLimitError';
    this.info = info;
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* fall through */
  }

  // Only treat as rate-limit if the rate-limiter set its scope header.
  // A 503 without that header is an upstream/config error (e.g., missing API key).
  const rateScope = res.headers.get('X-RateLimit-Scope');
  if ((res.status === 429 || res.status === 503) && rateScope) {
    const scope: RateLimitInfo['scope'] =
      rateScope === 'daily' || rateScope === 'global' ? rateScope : 'burst';
    const resetAt = parseInt(res.headers.get('X-RateLimit-Reset') || '0', 10) || 0;
    throw new RateLimitError({
      scope,
      message: data?.error || 'Rate limit exceeded',
      resetAt,
    });
  }
  if (!res.ok) {
    throw new Error(data?.error || `Request failed (${res.status})`);
  }
  return data as T;
}

export function scrapeUrl(url: string): Promise<ScrapeResult> {
  return postJson<ScrapeResult>('/api/scrape', { url });
}

export function detectTypes(
  html: string,
  sourceUrl?: string
): Promise<{ facts: PageFacts; detection: DetectionResult; score: SchemaScore; sourceUrl: string }> {
  return postJson('/api/detect', { html, sourceUrl });
}

export function generateForType(
  html: string,
  type: string,
  sourceUrl?: string,
  augment?: boolean
): Promise<GenerationResult> {
  return postJson<GenerationResult>('/api/generate', { html, type, sourceUrl, augment });
}
